import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { buildMyGroupSummary, type MyGroupSummary } from '@/lib/competitions/myGroup';
import { getBeneficiaryCompetitions } from '@/lib/firebase/queries/competitions';
import {
  loadCompetitionStandings,
  loadContainerResolutions,
  loadStandingUsers,
  standingUserName,
} from '@/lib/firebase/queries/competitionStandings';
import type { Competition } from '@/types/competitions';

export interface MyGroupResult {
  summary: MyGroupSummary;
  /** Member names only (never phones or emails). */
  names: Map<string, string>;
  /** The competition the ranks come from; null when there is none to rank by. */
  competition: (Pick<Competition, 'id' | 'name'> & { hasEnded: boolean }) | null;
}

/** userIds with a groupMembers row in `groupId` — the same read the group chat's member list makes. */
async function fetchGroupMemberIds(groupId: string): Promise<string[]> {
  const snap = await getDocs(query(collection(db, 'groupMembers'), where('groupId', '==', groupId)));
  const ids = snap.docs.map((d) => d.data().userId as string | undefined).filter((id): id is string => !!id);
  return Array.from(new Set(ids));
}

/**
 * The beneficiary's group, section, members and ranks («مجموعتي», namat#36,
 * mobile namat-mobile#17 getMyGroup). Ranks come from the competition the
 * beneficiary sees first (running before recently ended), read exactly like
 * the competition card (loadCompetitionStandings). Without one (or with
 * `withRanking` off) the group, section and members still come back, unranked.
 * null when the beneficiary has no group in this organization.
 */
export async function getMyGroup(
  organizationId: string,
  currentUserId: string,
  language: string,
  { withRanking = true }: { withRanking?: boolean } = {}
): Promise<MyGroupResult | null> {
  if (!organizationId || !currentUserId) return null;
  const competition = withRanking ? (await getBeneficiaryCompetitions(organizationId))[0] ?? null : null;
  const standings = competition
    ? await loadCompetitionStandings(competition.id, organizationId, currentUserId, competition)
    : null;
  const { resolutions, groupsById } = standings ?? (await loadContainerResolutions(organizationId, [currentUserId]));

  const myContainer = resolutions.get(currentUserId);
  if (!myContainer?.groupId) return null;
  const groupMemberIds = await fetchGroupMemberIds(myContainer.groupId);
  const summary = buildMyGroupSummary({
    currentUserId,
    myContainer,
    groupsById,
    groupMemberIds,
    competition: standings ? { perUser: standings.individuals, resolutions } : null,
  });
  if (!summary) return null;
  const users = await loadStandingUsers(summary.members.map((m) => m.userId));
  const names = new Map(
    summary.members.map((m) => [m.userId, standingUserName(users.get(m.userId), language)] as const)
  );
  return {
    summary,
    names,
    competition: competition ? { id: competition.id, name: competition.name, hasEnded: competition.hasEnded } : null,
  };
}
